import React, { useEffect } from 'react';
import moment from 'moment';
import { useStore } from '../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services

// props
import { IIncomeInvoiceResourceShortProps } from '../../../../props/finance/incomes/invoices/invoice';
import { IIncomeInvoicePartResourceShortProps } from '../../../../props/finance/incomes/invoices/part';

// components
import { DatePicker, DefaultButton, Dropdown, DropdownMenuItemType, Stack } from '@fluentui/react';
import GeneralService from '../../../../services/general';
import { IOrderResourceShortProps } from '../../../../props/orders/order';
import ErrorService from '../../../../services/general/error';
import IncomesDashboardFilter, { FilterOptionProps } from './filter';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import LoadingComponent from '../../../feedbacks/loading';
import IncomeInvoiceService from '../../../../services/finance/incomes/invoices/invoice';
import IncomeInvoicePartService from '../../../../services/finance/incomes/invoices/part';
import OrderService from '../../../../services/orders/order';

export type DateRangeFilterProps = {
    key: string;
    startDate: string;
    endDate: string;
}

export type AdvancedFilterProps = {
    key: string;
    title: string;
    selected: FilterOptionProps[];
}

export type IncomesDashboardDataProps = {
    invoices: IIncomeInvoiceResourceShortProps[];
    parts: IIncomeInvoicePartResourceShortProps[];
    orders: IOrderResourceShortProps[];
}

type SummaryProps = {
    orders: {
        outstanding: number;
        noInvoice: number;
    };
    invoices: {
        outstanding: number;
        overdue: number;
    };
}

type IncomesDashboardProps = {
}

const IncomesDashboard: React.FC<IncomesDashboardProps> = (props: IncomesDashboardProps) => {
    const { banner } = useStore();

    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [data, setData] = React.useState<IncomesDashboardDataProps>({ invoices: [], parts: [], orders: [] });
    const [summary, setSummary] = React.useState<SummaryProps>({
        orders: { outstanding: 0, noInvoice: 0 },
        invoices: { outstanding: 0, overdue: 0 }
    });
    const [filters, setFilters] = React.useState<AdvancedFilterProps[]>([]);
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const [dateRange, setDateRange] = React.useState<DateRangeFilterProps>({ key: 'thisWeek', startDate: moment().startOf('week').toISOString(), endDate: moment().endOf('week').toISOString() });
    const [basedOn, setBasedOn] = React.useState<string>('createdDate');

    useEffect(() => {
        refresh();
    }, [dateRange]);

    useEffect(() => {
        _onFilterChanged(filters);
    }, [filters]);

    useEffect(() => {
        return;
    }, []);

    const _onFilterChanged = (filters: AdvancedFilterProps[]) => {

    }

    const getOrders = async () => {
        let pageNumber: number = 1;
        let results: IOrderResourceShortProps[] = [];

        const qs: string[] = [];
        qs.push("top=100");
        qs.push("payment_status=unpaid");
        qs.push("order_status=active;completed");
        if (dateRange.key !== 'all') {
            qs.push('start_date=' + dateRange.startDate);
            qs.push('end_date=' + dateRange.endDate);
        }

        while (pageNumber > 0) {
            const _orders = await OrderService.retrieve([...qs, 'page=' + pageNumber].join("&"));
            results = [...results, ..._orders.data];

            if (_orders.pagination.nextPageUrl) {
                pageNumber += 1;
            } else {
                pageNumber = 0;
            }
        }

        return results;
    }

    const getInvoices = async () => {
        let pageNumber: number = 1;
        let results: IIncomeInvoiceResourceShortProps[] = [];

        const qs: string[] = [];
        qs.push("top=100");
        qs.push("status=Unpaid");
        if (basedOn === 'createdDate' && dateRange.key !== 'all') {
            qs.push('start_date=' + dateRange.startDate);
            qs.push('end_date=' + dateRange.endDate);
        } else if (basedOn === 'dueDate' && dateRange.key !== 'all') {
            qs.push('start_due_date=' + dateRange.startDate);
            qs.push('end_due_date=' + dateRange.endDate);
        }

        while (pageNumber > 0) {
            const _invoices = await IncomeInvoiceService.retrieve([...qs, 'page=' + pageNumber].join("&"));
            results = [...results, ..._invoices.data];

            if (_invoices.pagination.nextPageUrl) {
                pageNumber += 1;
            } else {
                pageNumber = 0;
            }
        }

        return results;
    }

    const getInvoiceParts = async () => {
        let pageNumber: number = 1;
        let results: IIncomeInvoicePartResourceShortProps[] = [];

        const qs: string[] = [];
        qs.push("top=100");
        qs.push("status=Unpaid");
        if (basedOn === 'createdDate' && dateRange.key !== 'all') {
            qs.push('start_date=' + dateRange.startDate);
            qs.push('end_date=' + dateRange.endDate);
        } else if (basedOn === 'dueDate' && dateRange.key !== 'all') {
            qs.push('start_due_date=' + dateRange.startDate);
            qs.push('end_due_date=' + dateRange.endDate);
        }

        while (pageNumber > 0) {
            const _invoices = await IncomeInvoicePartService.retrieve(undefined, [...qs, 'page=' + pageNumber].join("&"));
            results = [...results, ..._invoices.data];

            if (_invoices.pagination.nextPageUrl) {
                pageNumber += 1;
            } else {
                pageNumber = 0;
            }
        }

        return results;
    }

    const refresh = async () => {
        try {
            if (moment(dateRange.startDate).isAfter(moment(dateRange.endDate))) {
                return;
            }

            setLoaded(false);
            let _summary = {
                orders: { outstanding: 0, overdue: 0, noInvoice: 0 },
                invoices: { outstanding: 0, overdue: 0 }
            };

            const [_invoices, _parts, _orders] = await Promise.all([getInvoices(), getInvoiceParts(), getOrders()]);

            // get summary details
            _invoices.forEach((invoice) => {
                _summary.invoices.outstanding += Number(invoice.invoiceAmount);

                if (moment(invoice.dueDate).isBefore(moment())) {
                    _summary.invoices.overdue += Number(invoice.invoiceAmount);
                }
            });
            
            _orders.forEach((order) => {
                _summary.orders.outstanding += Number(order.invoiceAmount);
            });

            setSummary(_summary);
            setData({invoices: _invoices, parts: _parts, orders: _orders});

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_incomes_error',
                text: 'Failed to retrieve incomes. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
            setLoaded(true);
        }
    }

    return <Stack className={styles.container} styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 20 }}>
        {/* filter stack */}
        <Stack horizontal tokens={{ childrenGap: 20 }} horizontalAlign={'space-between'}>
            <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Dropdown label={"Based on"}
                    selectedKey={basedOn}
                    styles={{ root: { width: 120 } }}
                    disabled={!loaded}
                    options={[
                        { key: 'createdDate', text: 'Created date' },
                        { key: 'dueDate', text: 'Due date' },
                    ]} onChange={(_ev, opt) => {
                        setBasedOn((opt?.key || "createdDate") as string);
                    }} />
                <Dropdown label={"Date range"}
                    selectedKey={dateRange.key}
                    styles={{ root: { width: 160 } }}
                    disabled={!loaded}
                    options={[
                        { key: 'all', text: 'All' },
                        { key: 'divider_0', text: '', itemType: DropdownMenuItemType.Divider },
                        { key: 'thisWeek', text: 'This week' },
                        { key: 'lastWeek', text: 'Last week' },
                        { key: 'divider_1', text: '', itemType: DropdownMenuItemType.Divider },
                        { key: 'thisMonth', text: 'This month' },
                        { key: 'lastMonth', text: 'Last month' },
                        { key: 'last3Month', text: 'Last 3 month' },
                        { key: 'last6Month', text: 'Last 6 month' },
                        { key: 'divider_2', text: '', itemType: DropdownMenuItemType.Divider },
                        { key: 'thisYear', text: 'This year' },
                        { key: 'lastYear', text: 'Last year' },
                        { key: 'divider_3', text: '', itemType: DropdownMenuItemType.Divider },
                        { key: 'custom', text: 'Custom' }
                    ]} onChange={(_ev, opt) => {
                        let startDate = dateRange.startDate;
                        let endDate = dateRange.endDate;

                        if (opt?.key === 'thisWeek') {
                            startDate = moment().startOf('week').toISOString();
                            endDate = moment().endOf('week').toISOString();
                        } else if (opt?.key === 'lastWeek') {
                            startDate = moment().startOf('week').add(-1, 'week').toISOString();
                            endDate = moment().endOf('week').add(-1, 'week').toISOString();
                        } else if (opt?.key === 'thisMonth') {
                            startDate = moment().startOf('month').toISOString();
                            endDate = moment().endOf('month').toISOString();
                        } else if (opt?.key === 'lastMonth') {
                            startDate = moment().startOf('month').add(-1, 'month').toISOString();
                            endDate = moment().endOf('month').add(-1, 'month').toISOString();
                        } else if (opt?.key === 'last3Month') {
                            startDate = moment().startOf('month').add(-2, 'month').toISOString();
                            endDate = moment().endOf('month').toISOString();
                        } else if (opt?.key === 'last6Month') {
                            startDate = moment().startOf('month').add(-5, 'month').toISOString();
                            endDate = moment().endOf('month').toISOString();
                        } else if (opt?.key === 'thisYear') {
                            startDate = moment().startOf('year').toISOString();
                            endDate = moment().endOf('year').toISOString();
                        } else if (opt?.key === 'lastYear') {
                            startDate = moment().startOf('year').add(-1, 'year').toISOString();
                            endDate = moment().endOf('year').add(-1, 'year').toISOString();
                        }

                        setDateRange({ key: opt?.key as string, startDate, endDate });
                    }} />
                {dateRange.key === 'custom' ? <>
                    <DatePicker label={"Start date"}
                        value={moment(dateRange.startDate).toDate()}
                        formatDate={GeneralService.formatDate}
                        styles={{ root: { width: 125 } }}
                        disabled={!loaded}
                        onSelectDate={(date) => {
                            const _dateRange = dateRange;
                            _dateRange.startDate = date ? moment(date).toISOString() : _dateRange.startDate;

                            setDateRange({ ..._dateRange });
                        }} />
                    <DatePicker label={"End date"}
                        value={moment(dateRange.endDate).toDate()}
                        formatDate={GeneralService.formatDate}
                        styles={{ root: { width: 125 } }}
                        disabled={!loaded}
                        onSelectDate={(date) => {
                            const _dateRange = dateRange;
                            _dateRange.endDate = date ? moment(date).toISOString() : _dateRange.endDate;

                            setDateRange({ ..._dateRange });
                        }} />
                </> : null}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 20 }}>
                <DefaultButton iconProps={{ iconName: "Filter" }}
                    disabled={!loaded}
                    text={'Advanced filter'}
                    onClick={() => setActiveSurface('advancedFilter')}
                    styles={{ root: { marginTop: 25 } }} />
            </Stack>
        </Stack>
        <Stack className={'divider'} />
        <Stack horizontal tokens={{ childrenGap: 40 }}>
            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { width: '50%' } }}>
                <Label size={'large'}>Invoices</Label>
                <Stack horizontal styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 20 }}>
                    <Stack className='card' styles={{ root: { width: '33%' } }}>
                        <Text size={'medium'}>Outstanding Invoices</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-blue'} size={'xlarge'}>{GeneralService.getNumberWithSeparator(data.invoices.length)}</Label> : null}
                    </Stack>
                    <Stack className='card' styles={{ root: { width: '33%' } }}>
                        <Text size={'medium'}>Outstanding Amount</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-yellow'} size={'xlarge'}>Rp. {GeneralService.getNumberWithSeparator(summary.invoices.outstanding)}</Label> : null}
                    </Stack>
                    <Stack className='card' styles={{ root: { width: '33%' } }}>
                        <Text size={'medium'}>Overdue Amount</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-red'} size={'xlarge'}>Rp. {GeneralService.getNumberWithSeparator(summary.invoices.overdue)}</Label> : null}
                    </Stack>
                </Stack>
            </Stack>
            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { width: '50%' } }}>
                <Label size={'large'}>Orders</Label>
                <Stack horizontal styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 20 }}>
                    {/*<Stack className='card' styles={{ root: { width: '33%' } }}>
                        <Text size={'medium'}>Orders Without Invoice</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-blue'} size={'xlarge'}>{GeneralService.getNumberWithSeparator(data.orders.length)}</Label> : null}
                    </Stack>*/}
                    <Stack className='card' styles={{ root: { width: '33%' } }}>
                        <Text size={'medium'}>Outstanding Orders</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-yellow'} size={'xlarge'}>{GeneralService.getNumberWithSeparator(data.orders.length)}</Label> : null}
                    </Stack>
                    <Stack className='card' styles={{ root: { width: '33%' } }}>
                        <Text size={'medium'}>Outstanding Amount</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-red'} size={'xlarge'}>Rp. {GeneralService.getNumberWithSeparator(summary.orders.outstanding)}</Label> : null}
                    </Stack>
                    <Stack styles={{root: {width: '33%'}}}></Stack>
                </Stack>
            </Stack>
        </Stack>
        {activeSurface === 'advancedFilter' ? <IncomesDashboardFilter filters={filters} onDismiss={(filters?: AdvancedFilterProps[]) => {
            if (filters) { setFilters(filters); }
            setActiveSurface(undefined);
        }} /> : null}
    </Stack>;
};

export default IncomesDashboard;
